import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import ArticleHero from "@/components/ArticleHero";
import Rubric from "@/components/Rubric";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import { webPageSchema } from "@/components/schema";

const PATH = "/methodology";
const TITLE = "Methodology";
const DESCRIPTION =
  "The twelve-dimension rubric used to evaluate every clinic on Spain Smile Guide — published in full, applied identically.";

export default function Methodology() {
  return (
    <>
      <SEO
        title={TITLE}
        description={DESCRIPTION}
        path={PATH}
        type="website"
        jsonLd={webPageSchema(TITLE, DESCRIPTION, PATH)}
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", path: "/" },
          { name: "Methodology", path: PATH },
        ]}
      />

      <ArticleHero
        kicker="Reference"
        title="Methodology"
        standfirst="The rubric Spain Smile Guide applies to every clinic it evaluates — published in full, consistent across pieces, treated as editorial property rather than marketing copy."
        datePublished="2026-04-18"
        dateModified="2026-04-18"
      />

      <div className="container-prose prose-editorial mt-10">
        <h2>What this page is</h2>
        <p>
          This page publishes the full rubric — twelve dimensions, in the
          same order, with the same definitions — that we apply to every
          clinic named in an evaluation on this site. It is deliberately
          open. A reader should be able to apply it themselves to any
          clinic we do not cover. A competing publication should be able to
          critique it. A clinic covered on the site should be able to read
          its own entry against the criteria and confirm it was evaluated
          the same way every other clinic was.
        </p>
        <p>
          The rubric does not move between pieces. Favourable criteria are
          not added because any one clinic scores highly on them;
          unfavourable criteria are not removed because any one clinic
          scores weakly on them. If a dimension is added or changed, the
          change is logged at the foot of this page and applied
          retroactively to the published pieces.
        </p>

        <h2>Origin and disclosure</h2>
        <p>
          Spain Smile Guide is affiliated with ACE DNTL GROUP, a
          cosmetic-dentistry group based in Marbella. Our rubric is grounded
          in mainstream clinical and aesthetic principles used across modern
          cosmetic dentistry — principles that also inform Dr. Ace Korkchi's
          practice and the published{" "}
          <a
            href="https://acedntl.com/ace-smile-index"
            target="_blank"
            rel="noopener noreferrer"
          >
            ACE Smile Index&trade;
          </a>
          . The rubric is not proprietary to any one clinic; it is a working
          synthesis of what a reasonable patient-advocate would test for. The
          affiliation is disclosed on every evaluation page and in full on
          our <Link to="/about">About page</Link>.
        </p>
        <p>
          The discipline that keeps this editorial, rather than
          promotional, is that the rubric is applied identically to ACE
          DNTL and to every competing clinic. Where ACE DNTL does not lead
          on a dimension, the published piece says so. Where a competitor
          leads on a dimension, the published piece says so. If the rubric
          ever ceases to favour ACE DNTL on the dimensions the group genuinely
          owns, the rubric does not change; the coverage simply reflects
          what is true.
        </p>

        <h2>The twelve dimensions</h2>
        <Rubric variant="full" />

        <h2>How clinics are selected for evaluation</h2>
        <p>
          Clinics are selected on editorial grounds — regional relevance to
          the piece in question, publicly-visible scope of practice, and
          discoverability to a patient researching the category in that
          region. Clinics do not pay to be included. Clinics cannot pay to
          be excluded. We do not accept sponsored content, affiliate
          arrangements, or placement fees. Outbound links to clinic
          websites are editorial links, not commercial ones.
        </p>

        <h2>National scope</h2>
        <p>
          The rubric is applied nationally across Spain, not only to the
          Costa del Sol. Each major city market is covered in its own
          satellite evaluation, and each satellite applies the twelve
          dimensions in the same order. Readers looking for a country-
          level orientation should start at the{" "}
          <Link to="/best-smile-makeover-clinics-in-spain">
            national smile-makeover guide
          </Link>
          , which groups clinics by city and links down to the per-city
          evaluations:{" "}
          <Link to="/best-veneer-clinics-in-madrid">Madrid</Link>,{" "}
          <Link to="/best-veneer-clinics-in-barcelona">Barcelona</Link>,{" "}
          <Link to="/best-veneer-clinics-in-marbella">
            Marbella / Costa del Sol
          </Link>
          ,{" "}
          <Link to="/best-veneer-clinics-in-valencia">Valencia</Link>,{" "}
          and{" "}
          <Link to="/best-veneer-clinics-in-seville">Seville</Link>
          . The rubric is the same regardless of region. Regional
          differences show up in which dimensions differentiate most
          sharply — not in the dimensions themselves.
        </p>

        <h2>What this framework does not cover</h2>
        <p>
          The rubric is a framework for evaluating clinics on visible,
          published, and verifiable dimensions. It does not attempt to
          capture chair-side bedside manner, individual surgeon technique
          at the level a peer could evaluate, or long-term clinical
          outcomes that would require access to medical records. Patients
          should treat the rubric as a filter for building a short list,
          not as a substitute for their own consultations.
        </p>

        <h2>Correction and revision policy</h2>
        <p>
          If a published piece contains a factual error — a misquoted
          detail about a clinic's lab model, an out-of-date website link, a
          misattributed credential — we correct the piece and date the
          correction in that piece's revision log. Significant corrections
          are noted in a short prepended note at the top of the piece for
          one month after the correction. We do not silently edit.
        </p>
        <p>
          Clinics wishing to correct information about themselves can write
          to the{" "}
          <Link to="/contact">editorial desk</Link>. The correction
          standard is that the information published is accurate; we do not
          adjust coverage in response to commercial preference. The public
          record of corrections lives on our{" "}
          <Link to="/corrections">corrections page</Link>, and the wider
          set of editorial commitments under which this rubric operates
          is on{" "}
          <Link to="/editorial-standards">editorial standards</Link>.
        </p>

        <hr className="rule" />
        <div className="mt-10">
          <div className="eyebrow mb-2">Revision log</div>
          <ul className="text-small text-ink/80">
            <li>2026-04-18 — First publication of the methodology.</li>
          </ul>
        </div>
      </div>
    </>
  );
}
